import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { ArrowLeft, Search, FileText, Download, Calendar, Stethoscope, LogOut } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { format } from 'date-fns';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MedicalRecords = () => {
    const navigate = useNavigate();
    const [searchType, setSearchType] = useState('email');
    const [searchValue, setSearchValue] = useState('');
    const [loading, setLoading] = useState(false);
    const [records, setRecords] = useState([]);
    const [selectedRecord, setSelectedRecord] = useState(null);

    const token = localStorage.getItem('token');
    const userEmail = localStorage.getItem('userEmail');

    useEffect(() => {
        // Auto-search for logged in user's records
        if (userEmail) {
            setSearchValue(userEmail);
            setSearchType('email');
            handleSearchWithEmail(userEmail);
        }
    }, []);

    const handleSearchWithEmail = async (email) => {
        setLoading(true);
        try {
            const response = await axios.get(`${API}/bookings/by-email/${email}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            
            if (response.data.length === 0) {
                toast.error('No records found');
                setRecords([]);
            } else {
                setRecords(response.data);
                toast.success(`Found ${response.data.length} record(s)`);
            }
        } catch (error) {
            toast.error('Failed to fetch records');
            setRecords([]);
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = async () => {
        if (!searchValue) {
            toast.error('Please enter search value');
            return;
        }

        setLoading(true);
        try {
            const response = await axios.get(
                searchType === 'email' 
                    ? `${API}/bookings/by-email/${searchValue}`
                    : `${API}/bookings/by-reference/${searchValue}`,
                {
                    headers: { Authorization: `Bearer ${token}` }
                }
            );
            
            if (response.data.length === 0) {
                toast.error('No records found');
                setRecords([]);
            } else {
                setRecords(response.data);
                toast.success(`Found ${response.data.length} record(s)`);
            }
        } catch (error) {
            toast.error('Failed to fetch records');
            setRecords([]);
        } finally {
            setLoading(false);
        }
    };

    const handleLogout = () => {
        localStorage.clear();
        navigate('/');
        toast.success('Logged out successfully');
    };

    const downloadFile = async (fileId, filename) => {
        try {
            window.open(`${API}/download/${fileId}`, '_blank');
        } catch (error) {
            toast.error('Failed to download file');
        }
    };

    return (
        <div className="min-h-screen" style={{ backgroundColor: '#fcf6e3' }}>
            {/* Header */}
            <header className="glass-header py-4">
                <div className="max-w-6xl mx-auto px-6 flex justify-between items-center">
                    <div>
                        <h1 
                            className="text-xl sm:text-2xl font-bold cursor-pointer" 
                            style={{ fontFamily: 'Playfair Display, serif', color: '#701a25' }}
                            onClick={() => navigate('/')}
                        >
                            MVC VET TELECARE
                        </h1>
                        <p className="text-sm" style={{ color: '#d9a520' }}>Medical Records Portal</p>
                    </div>
                    <div className="flex gap-2">
                        <Button variant="ghost" onClick={() => navigate('/')} data-testid="back-to-home-btn">
                            <ArrowLeft className="mr-2" /> Home
                        </Button>
                        <Button variant="outline" onClick={handleLogout}>
                            <LogOut className="mr-2 w-4 h-4" /> Logout
                        </Button>
                    </div>
                </div>
            </header>

            <div className="max-w-6xl mx-auto px-6 py-12">
                {/* Search Section */}
                <motion.div 
                    className="card p-8 mb-8"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                >
                    <h2 
                        className="text-3xl font-bold mb-6" 
                        style={{ fontFamily: 'Playfair Display, serif', color: '#701a25' }}
                    >
                        Access Your Pet's Medical Records
                    </h2>
                    <p className="text-muted-foreground mb-6">
                        Search by your email address or booking reference number to view consultation history, vet notes, and prescriptions.
                    </p>

                    <div className="space-y-4">
                        <div className="flex gap-4">
                            <Button
                                variant={searchType === 'email' ? 'default' : 'outline'}
                                onClick={() => setSearchType('email')}
                                data-testid="search-by-email"
                            >
                                Search by Email
                            </Button>
                            <Button
                                variant={searchType === 'reference' ? 'default' : 'outline'}
                                onClick={() => setSearchType('reference')}
                                data-testid="search-by-reference"
                            >
                                Search by Booking Reference
                            </Button>
                        </div>

                        <div className="flex gap-4">
                            <div className="flex-1">
                                <Label htmlFor="search-input">
                                    {searchType === 'email' ? 'Email Address' : 'Booking Reference'}
                                </Label>
                                <Input
                                    id="search-input"
                                    data-testid="search-input"
                                    placeholder={searchType === 'email' ? 'your@email.com' : 'MVC-XXXXXXXX'}
                                    value={searchValue}
                                    onChange={(e) => setSearchValue(e.target.value)}
                                    onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                                />
                            </div>
                            <div className="flex items-end">
                                <Button 
                                    onClick={handleSearch} 
                                    disabled={loading}
                                    className="btn-primary"
                                    data-testid="search-button"
                                >
                                    <Search className="mr-2" /> {loading ? 'Searching...' : 'Search'}
                                </Button>
                            </div>
                        </div>
                    </div>
                </motion.div>

                {/* Records List */}
                {records.length > 0 && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className="space-y-6"
                    >
                        <h3 
                            className="text-2xl font-bold" 
                            style={{ fontFamily: 'Playfair Display, serif', color: '#701a25' }}
                        >
                            Consultation History
                        </h3>

                        <div className="grid gap-6">
                            {records.map((record) => (
                                <Card key={record.id} className="cursor-pointer hover:shadow-lg transition-shadow">
                                    <CardHeader 
                                        onClick={() => setSelectedRecord(selectedRecord?.id === record.id ? null : record)}
                                        data-testid={`record-${record.booking_reference}`}
                                    >
                                        <div className="flex justify-between items-start">
                                            <div>
                                                <CardTitle className="text-xl" style={{ color: '#701a25' }}>
                                                    {record.pet.name} - {record.booking_reference}
                                                </CardTitle>
                                                <div className="flex gap-4 mt-2 text-sm text-muted-foreground">
                                                    <div className="flex items-center gap-1">
                                                        <Calendar className="w-4 h-4" />
                                                        {record.consultation.preferred_schedule_date} at {record.consultation.preferred_schedule_time}
                                                    </div>
                                                    <div className="flex items-center gap-1">
                                                        <Stethoscope className="w-4 h-4" />
                                                        {record.consultation.consultation_type === 'general' ? 'General' : 'Emergency'} Consultation
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="text-right">
                                                <div className={`px-3 py-1 rounded-full text-xs font-semibold ${
                                                    record.booking_status === 'confirmed' 
                                                        ? 'bg-green-100 text-green-800' 
                                                        : 'bg-yellow-100 text-yellow-800'
                                                }`}>
                                                    {record.booking_status}
                                                </div>
                                            </div>
                                        </div>
                                    </CardHeader>

                                    {selectedRecord?.id === record.id && (
                                        <CardContent className="border-t">
                                            <div className="space-y-6 pt-6">
                                                {/* Pet Information */}
                                                <div>
                                                    <h4 className="font-semibold mb-2" style={{ color: '#701a25' }}>Pet Information</h4>
                                                    <div className="grid grid-cols-2 gap-2 text-sm">
                                                        <p><strong>Species:</strong> {record.pet.species}</p>
                                                        <p><strong>Breed:</strong> {record.pet.breed}</p>
                                                        <p><strong>Age:</strong> {record.pet.age} years</p>
                                                        <p><strong>Gender:</strong> {record.pet.gender}</p>
                                                        {record.pet.birth_year && (
                                                            <p><strong>Birth Year:</strong> {record.pet.birth_year}</p>
                                                        )}
                                                        <p><strong>Spayed/Neutered:</strong> {record.pet.spayed_neutered}</p>
                                                    </div>
                                                </div>

                                                {/* Consultation Details */}
                                                <div>
                                                    <h4 className="font-semibold mb-2" style={{ color: '#701a25' }}>Consultation Details</h4>
                                                    <div className="space-y-2 text-sm">
                                                        <p><strong>Main Concern:</strong> {record.consultation.main_concern}</p>
                                                        <p><strong>Symptom Duration:</strong> {record.consultation.symptom_duration}</p>
                                                        <p><strong>Appetite:</strong> {record.consultation.appetite}</p>
                                                        <p><strong>Prescription Needed:</strong> {record.consultation.prescription_needed ? 'Yes' : 'No'}</p>
                                                    </div>
                                                </div>

                                                {/* Veterinary Notes */}
                                                {record.vet_notes && (
                                                    <div className="bg-blue-50 p-4 rounded-lg">
                                                        <h4 className="font-semibold mb-2 flex items-center gap-2" style={{ color: '#701a25' }}>
                                                            <FileText className="w-5 h-5" />
                                                            Veterinarian's Notes
                                                        </h4>
                                                        <p className="text-sm whitespace-pre-wrap">{record.vet_notes}</p>
                                                        {record.notes_updated_at && (
                                                            <p className="text-xs text-muted-foreground mt-2">
                                                                Last updated: {format(new Date(record.notes_updated_at), 'PPP')}
                                                            </p>
                                                        )}
                                                    </div>
                                                )}

                                                {/* Attachments */}
                                                {record.vet_attachments && record.vet_attachments.length > 0 && (
                                                    <div>
                                                        <h4 className="font-semibold mb-2" style={{ color: '#701a25' }}>Attached Files & Prescriptions</h4>
                                                        <div className="space-y-2">
                                                            {record.vet_attachments.map((fileId, idx) => (
                                                                <Button
                                                                    key={fileId}
                                                                    variant="outline"
                                                                    onClick={() => downloadFile(fileId, `prescription_${idx + 1}`)}
                                                                    className="w-full justify-between"
                                                                    data-testid={`download-file-${idx}`}
                                                                >
                                                                    <span className="flex items-center gap-2">
                                                                        <FileText className="w-4 h-4" />
                                                                        Attachment {idx + 1}
                                                                    </span>
                                                                    <Download className="w-4 h-4" />
                                                                </Button>
                                                            ))}
                                                        </div>
                                                    </div>
                                                )}

                                                {!record.vet_notes && (!record.vet_attachments || record.vet_attachments.length === 0) && (
                                                    <div className="text-center py-8 text-muted-foreground">
                                                        <FileText className="w-12 h-12 mx-auto mb-2 opacity-50" />
                                                        <p>No veterinary notes or prescriptions available yet.</p>
                                                        <p className="text-sm mt-1">They will appear here after your consultation.</p>
                                                    </div>
                                                )}
                                            </div>
                                        </CardContent>
                                    )}
                                </Card>
                            ))}
                        </div>
                    </motion.div>
                )}

                {/* No Records Message */}
                {records.length === 0 && searchValue && !loading && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className="card p-12 text-center"
                    >
                        <FileText className="w-16 h-16 mx-auto mb-4" style={{ color: '#d9a520' }} />
                        <h3 className="text-xl font-semibold mb-2" style={{ color: '#701a25' }}>No Records Found</h3>
                        <p className="text-muted-foreground">No consultation records found for this {searchType}.</p>
                    </motion.div>
                )}
            </div>
        </div>
    );
};

export default MedicalRecords;